import {BookmarkDateRange, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {Segfilter, SegfilterSegmentation} from 'common/widgets/property-filter-menu/models/segfilter';
import {Segmentation} from 'common/types/reports/query';
import {FiltersOperator, FunnelsCountType} from 'common/query-builder/query-entry-section/types';
import {
  ChartType as CommonChartType,
  ChartVizType,
  ResourceType,
  SavedLegend,
  ValueRepresentationType,
} from 'common/report/constants';
import {ArrayType} from 'common/util/typescript';
import {SectionItem} from 'common/components/items-menu/types';
import {Segment} from 'common/query-builder/query-block/segment-menu/types';
import {ComparisonDateRangeBookmarkFormat} from 'common/query-builder/query-block/time-comparison-menu/types';
import {SortConfig} from 'common/report/util/sorting/types';
import {LiftComparison} from 'common/widgets/lift-comparison-menu/types';
import type {SelectMode} from 'common/charts/contexts/segment-filter-controller';

import {FrequencyViewType} from './frequency-chart/types';

export type ColorChoice = {key: string; color: string};

type BookmarkBaseStep = {
  event?: string;
  event_id?: number;
  custom_event?: number;
  custom_event_name?: string;
  step_label?: string;
  session_event?: `start` | `end`;

  /** for flows */
  forward?: number;
  /** for flows */
  reverse?: number;
};

/**
 * This is the "newer" format for property filters on steps, introduced
 * in 2018. There are still saved bookmarks where the steps implement
 * Segfilter instead, hence this combinable type.
 */
type PropertyFilterParams = {
  bool_op: FiltersOperator;
  property_filter_params_list: Segfilter[] | Segmentation[];
};

/** Range of steps to which an exclusion event applies */
export type StepRange = {
  to?: number;
  from?: number;
};

type ExclusionStepRange = {
  steps: StepRange;
};

/** Represents a combination step of many events.*/
export type BookmarkOrStep = {
  compare: BookmarkFunnelStep[];
};

export function isOrStep(step: ArrayType<FunnelBookmark['steps']>): step is BookmarkOrStep {
  return `compare` in step;
}

/**
 * Current representation of a single funnel step.
 *
 * Use this for bookmark steps generated by live code.
 * */
export type BookmarkFunnelStep = BookmarkBaseStep & PropertyFilterParams;
/** Historical funnels step that includes variations in filter structure. Only use this for serialization. */
export type LegacyBookmarkFunnelStep = BookmarkBaseStep & (PropertyFilterParams | Segfilter);
/** A single exclusion step. (no variation in filter structure) */
export type BookmarkFunnelExclusionStep = BookmarkFunnelStep & ExclusionStepRange;

export type LegacyAggregateBy = {raw: string; selector: string};
export type AggregateBy = LegacyAggregateBy | Segmentation;

export function isLegacyAggregateBy(step: ArrayType<FunnelBookmark['aggregate_by']>): step is LegacyAggregateBy {
  return `raw` in step;
}

export type BookmarkFilterByEvent = {
  operator: string;
  children: Array<
    | {
        [ResourceType.Event]: Segmentation;
      }
    | {
        [ResourceType.User]: Segmentation;
      }
    | {
        [ResourceType.Other]: Segmentation;
      }
  >;
};

/**
 * Representation of a serialized funnels bookmark.
 *
 * In certain attributes like steps, which have existed for a long time,
 * there are historical variations in the bookmark structure that we account for
 * to make sure our deserialization is typed and validated correctly.
 *
 * However, the bookmarks we produce today are a stable subset of this historical
 * format, and we should expose a type that represents that subset, since most bookmark
 * consumption code deals with client-generated bookmarks and not the entire history
 * of bookmarks.
 *
 * TODO: expose LegacyFunnelsBookmark and FunnelsBookmark, where FunnelsBookmark is a
 * subset of Legacy.
 */
export interface FunnelBookmark {
  action_mode?: 'first_touch' | 'last_touch' | 'step';
  // only set whten action_mode is `step`
  action_step?: number;
  /** better known as hold property constant */
  aggregate_by?: AggregateBy[];
  chart_type: ChartType;
  count_type: string;
  countType?: FunnelsCountType;
  data_group_id?: string;
  /** some old funnel bookmarks do not have a date range, and the API doesn't require one */
  date_range?: BookmarkDateRange;
  time_comparison?: ComparisonDateRangeBookmarkFormat;
  exclusions: BookmarkFunnelExclusionStep[];
  filter_by_cohort?: string; // stringified CohortFilterTree type
  filter_by_event?: BookmarkFilterByEvent;
  group_by?: Segment[];
  interval?: number;
  /** maximum number of segments to return in a query */
  limit?: number;
  length: number;
  length_unit: ConversionWindowUnit;
  time_buckets: {
    min: number;
    max: number;
    offset: number;
    bucket_size: number;
    is_drilled_down: boolean;
  };
  frequency_buckets: {
    min: number;
    max: number;
    offset: number;
    bucket_size: number;
  };
  funnel_order: string;
  is_step_any_order?: boolean[];
  stat_sig_control?: string[];
  trend_unit: TrendUnit;
  trend_count_type: TrendCountType;
  version?: number;
  steps: Array<BookmarkOrStep | LegacyBookmarkFunnelStep>;
  eventIndex?: number;
  /** A list of saved legend selections to utilize when displaying results.
   * The list is constructed in parallel with the bookmark's `group_by` property. (the first segment in the group by
   * corresponds with the first entry in the legend sections)
   * for more information please see:
   * https://www.notion.so/mpspeed/Saving-Selected-Segments-in-Reports-Dash-82f074988d024f54b18089537cd28e17
   */
  savedLegend?: SavedLegend[];
  /** @deprecated - use savedLegend instead
   * selected segments was the old way of saving legend items. However, it only allowed for a single level of selections,
   * so going forward we want to use savedLegend to allow for selecting of segments for multi-segmentation workloads.
   */
  selectedSegments?: ColorChoice[];
  /** @deprecated - selectMode has been moved to be session state living on SegmentFilterController. When a query is
   * persisted to bookmark, it'll implicitly always be SelectMode.Item */
  selectMode?: SelectMode;
  trendType?: TrendChartType;
  frequencyType?: FrequencyViewType;
  frequencyVizType?: ChartVizType;
  ttcVizType?: ChartVizType;
  stepsVizType?: ChartVizType;
  ttcValueRepresentation?: ValueRepresentationType;
  ttcAggregateType?: FunnelsAggregateType;
  frequencyAggregateType?: FunnelsAggregateType;
  frequencyValueRepresentation?: ValueRepresentationType;
  selectedTableColumns?: SelectedColumns;
  sortField?: string;
  sortOrder?: number;
  sorting?: SortConfig;
  liftComparison?: LiftComparison;
}

export interface FunnelBookmarkMetadata {
  time_label?: string;
  time_comparison_label?: string;
  trend_unit?: TimeUnit;
  trend_view_type?: TrendChartType;
  num_steps?: number;
  url?: string;
  chart_type?: string;
  num_days?: number;
  segmented?: boolean;
  selected_step?: number;
  select_mode?: SelectMode;
  selected_segments?: ColorChoice[];
  frequency_representation_type?: FrequencyViewType;
  time_buckets?: TODO;
  frequency_buckets?: TODO;
  frequency_value_representation?: ValueRepresentationType;
  ttc_value_representation?: ValueRepresentationType;
}

/** A collection of FunnelSegments for a single date */
export interface DateKeyedFunnelSegments {
  date: Date;
  segments: FunnelSegment[];
}

export interface FunnelSegment {
  /** all property values which make up the segment */
  props: SegmentId[];
  /** concatenated string property value, derived from props */
  prop: string;
  /** concatenated, renamed propNames from props */
  propName: string;
  /** funnel data */
  steps: Array<BasicFunnelStep | FunnelStep>;
}

export interface FunnelMedianDataSegment {
  value: number;
  label: string;
  prop: string;
  props: string[];

  comparisonValue?: number;
}

/** Maps unique segment identifiers ("prop") to the segment's details.
 * See FunnelSegment for descriptions of "prop", "props" and "propName" */
export type SegmentPropToDetailsMap = {
  [prop: string]: {
    props: SegmentId[];
    propName: string;
    steps: FunnelSegment['steps'];
  };
};

/** Identifier for a single segment value */
export interface SegmentId {
  /** raw string value of the segment */
  prop: string;
  /** user-friendly renaming of prop, suitable for printing */
  propName?: string;

  /**
   * Query parameters not encapsulated by the prop which are necessary to recreate
   * this segment.
   *
   * ex: bucketSize and index for a bucketed numeric segment
   */
  cohortDesc?: TODO;
}

export type FunnelData = Array<BasicFunnelStep | FunnelStep>;

/**
 * Basic funnel step information, always used for the first step.
 *
 * @see FunnelStep
 */
export interface BasicFunnelStep {
  /** mean time to convert; null for step 0 */
  avg_time: number | null;
  /** time to convert from first step */
  avg_time_from_start: number | null;
  /** number of conversions */
  count: number;
  /** only set if true */
  custom_event?: boolean;
  /** custom event identifier */
  custom_event_id?: number;
  /** name of the event */
  event: string;
  /** name of the event */
  goal: string;
  /** step index */
  index: number;
  /** conversion from start of funnel */
  overall_conv_ratio: number;
  /** conversion from previous step */
  step_conv_ratio: number;
  /** same as event OR custom event name */
  step_label: string;
}

/** A funnel step containing more detailed conversion information. Never represents the very first step. */
export interface FunnelStep extends BasicFunnelStep {
  /** median time from previous step. length = 1 */
  percentiles_from_prev?: number[];
  /** conversion times distribution from previous step */
  time_buckets_from_prev?: BucketData;

  /** median time from funnel start. length = 1 */
  percentiles_from_start?: number[];
  /** conversion times distribution from funnel start */
  time_buckets_from_start?: BucketData;
  /** p-value stats, only for last step. */
  p_value?: PValue;

  /** conversion frequencies for dropoff */
  dropoff_step_freq_buckets?: BucketData;
  /** median time for dropoff frequencies. length = 1 */
  dropoff_step_freq_percentiles?: number[];

  /** conversion frequencies for conversion */
  convert_step_freq_buckets?: BucketData;
  /** median time for conversion frequencies.  length = 1 */
  convert_step_freq_percentiles?: number[];

  /** conversion frequencies for conversion rate */
  convert_rate_freq_buckets?: BucketData;
  /** median time for conversion frequencies.  length = 1 */
  convert_rate_freq_percentiles?: number[];
}

export interface BucketData {
  /** Number of entries in each bucket. Says nothing about bucket size. */
  buckets: number[];
  /** Number of conversions below the window represented by buckets. */
  lower: number;
  /** Number of conversions above the window represented by buckets. */
  higher: number;
}

export enum ChartType {
  // Aka "Funnel Steps"
  Chart = `chart`,
  Trends = `trends`,
  TimeToConvert = `time-to-convert`,
  Frequency = `frequency`,
  Sankey = `sankey`,
}

export const ChartTypeDisplayName: {[key in ChartType]: string} = {
  [ChartType.Chart]: `Funnel Steps`,
  [ChartType.Trends]: `Funnel Trends`,
  [ChartType.TimeToConvert]: `Time to Convert`,
  [ChartType.Frequency]: `Frequency`,
  [ChartType.Sankey]: `Top Paths`,
};

export const CommonToFunnelChartTypeMap: {[key: string]: ChartType} = {
  [CommonChartType.FunnelSteps]: ChartType.Chart,
  [CommonChartType.FunnelStepsMetric]: ChartType.Chart,
  [CommonChartType.FunnelTrend]: ChartType.Trends,
  [CommonChartType.FunnelMedianTTC]: ChartType.TimeToConvert,
  [CommonChartType.FunnelTTCLine]: ChartType.TimeToConvert,
  [CommonChartType.FunnelTTCBar]: ChartType.TimeToConvert,
  [CommonChartType.Frequency]: ChartType.Frequency,
  [CommonChartType.FunnelFrequencyLine]: ChartType.Frequency,
  [CommonChartType.FunnelFrequencyBar]: ChartType.Frequency,
  [CommonChartType.FunnelTopPaths]: ChartType.Sankey,
};

export const FunnelToCommonChartTypeMap: {[key in ChartType]: CommonChartType} = {
  [ChartType.Chart]: CommonChartType.FunnelSteps,
  [ChartType.Trends]: CommonChartType.FunnelTrend,
  [ChartType.TimeToConvert]: CommonChartType.FunnelMedianTTC,
  [ChartType.Frequency]: CommonChartType.Frequency,
  [ChartType.Sankey]: CommonChartType.FunnelTopPaths,
};

export const FunnelsChartSelections = Object.values(FunnelToCommonChartTypeMap);

export interface CompleteSegfilter {
  op: `and` | `or`;
  filters: Array<Segfilter | SegfilterSegmentation>;
}

export interface ConversionWindow {
  unit: ConversionWindowUnit;
  value: number;
}

export enum ConversionWindowUnit {
  Second = `second`,
  Minute = `minute`,
  Hour = `hour`,
  Day = `day`,
  Week = `week`,
  Month = `month`,
  Session = `session`,
}

export {FunnelsCountType as CountType};

export enum FunnelOrder {
  Loose = `loose`,
  Any = `any`,
}

export interface IncompleteBuckets {
  first: boolean;
  last: boolean;
}

export interface PValue {
  /** stat sig: 1 - p */
  confidence: number;
  /** stat sig */
  p: number;
  /** whether there were sufficient samples to be stat sig */
  insufficient_samples: boolean;
  /** whether this p value should be shown as interesting */
  is_interesting: boolean;
}

export type FunnelSelectionChangeEventDetail = {
  selected: string[];
  selectMode: SelectMode;
  sortOrder?: number;
  sortField?: string;
};

export type FunnelSelectionChangeEvent = CustomEvent<FunnelSelectionChangeEventDetail>;

export interface StepSelection {
  isEntireRange: boolean;
  range: {from?: number; to?: number};
}

export enum TrendUnit {
  Minute = `minute`,
  Hour = `hour`,
  Day = `day`,
  Week = `week`,
  Month = `month`,
  Quarter = `quarter`,
}

export enum TrendCountType {
  Linear = `linear`,
  Cumulative = `cumulative`,
}

/** Trend (overtime) data organized as segment to all dates:values */
export interface TrendDateValueData {
  [segment: string]: {
    [date: string]: number;
  };
}

/** All trend chart view types available  */
export enum TrendChartType {
  Conversion = `conversion`,
  TimeToConvert = `time-to-convert`,
  TopOfFunnel = `top-of-funnel`,
  BottomOfFunnel = `bottom-of-funnel`,
}

export enum CohortType {
  STEP_DROP_OFF = `step-drop-off`,
  CUMULATIVE_DROP_OFF = `cumulative-drop-off`,
  CONVERTED = `converted`,
  TIME_BUCKET = `time-bucket`,
  FREQUENCY_CONVERSION = `frequency-conversion`,
  FREQUENCY_DROP_OFF = `frequency-drop-off`,
}

/** All metrics that can be shown in the table */
export enum TableToggleableMetrics {
  StatSig = `stat-sig`,
  ConvFirstStep = `conv-first-step`,
  ConvPrevStep = `conv-prev-step`,
  Count = `count`,
  TimeFirstStep = `time-first-step`,
  TimePrevStep = `time-prev-step`,
}

export enum TableNonToggleableMetrics {
  OverallConv = `conv-overall`,
  Segment = `segment`,
  /** psuedo metric that is shown when there are no other metrics to show. */
  NoMetrics = `no-metrics`,
}
export type TableMetrics = TableToggleableMetrics | TableNonToggleableMetrics;

export type SelectedColumns = {[key in TableToggleableMetrics]: boolean};

export enum FunnelsAggregateType {
  Distribution = `Distribution`,
  Median = `Median`,
}

export const FunnelsAggregateTypeDisplayName: {[key in FunnelsAggregateType]: Partial<SectionItem>} = {
  [FunnelsAggregateType.Distribution]: {
    label: `Distribution`,
  },
  [FunnelsAggregateType.Median]: {
    label: `Median`,
  },
};
